import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DealDto, {
  DealAccountDto,
  DealAccountServiceDto,
  DealAccountType,
  DealAdditionalInfoDto,
  DealCargoDto,
  DealCargoPackingElementDto,
  DealCargoPackingListDto,
  DealCargoPackingSubElementDto,
  DealDocumentDto,
  DealStatus,
  DealSegmentDto,
  DealTrackerDto,
  DealBasicInfoDto,
  DealAnomalyType,
  DealAccountStatus,
  DealPaymentHistoryDto,
  DealTransportationStageDto,
  DealTransportationStagePlanDto,
  DealTransportationStagePlanStageDto,
  DealLiteDto,
  DealCargoPackingDestinationMode,
  DealCargoDestinationAssignmentDto,
  DealCargoOriginAssignmentDto,
  OldDealSegmentDto,
  DealVehicleDto,
  DealLiteApplicationDto,
  DealRoutesDto,
  DealPlannedRoutePointDto,
  DealPlannedRoutePointsDto,
  TrackingSensorDto,
} from "../../dto/Deal/DealDto";
import { ApplicationCalculationModel, ApplicationDataConverter, ApplicationModel } from "../Applications/ApplicationModel";
import { CurrencyModel } from "../Settings/CurrencyModel";
import { TransportCharacteristicModel } from "../Settings/TransportCharacteristicModel";
import { AttachDocumentFormModel, AttachDocumentDataConverter } from "../Documents/AttachDocumentModel";
import AccountPaymentModel from "./AccountPaymentModel";
import AccountClosingDocumentModel, { AccountClosingDocumentDataConverter } from "./AccountClosingDocumentModel";
import exhaustiveCheck from "../../utils/exhaustiveCheck";
import i18n from "../../i18n";
import OverheadExpenseModel, { OverheadExpenseDataConverter } from "../OverheadExpenses/OverheadExpenseModel";
import TableUtils from "../../utils/TableUtils";
import { SupportedLanguages } from "../Settings/TranslationModel";
import { blue, gold, gray, green, purple, red } from "@ant-design/colors";
import DealNotificationModel, { DealNotificationDataConverter } from "../Notification/DealNotificationModel";
import VatUtils from "../../utils/VatUtils";
import {
  CreateCargoInputForm,
  EditCargoInputForm,
  FormPackingList,
} from "../../containers/DealPage/components/Configuration/Cargo/components/types";
import { CreateCargoRequestDto, SegmentsReplaceAllRequestDto } from "../../api/apiServices/backOffice/controllers/deals/deals.requestsDto";
import {
  DealTransportationStagePlanStageUpdateRequestDto,
  CopyDealTransportationStagePlanRequestDto,
  UpdateDealTransportationStagePlanRequestDto,
} from "../../api/apiServices/backOffice/controllers/dealTransportationStagePlans/dealTransportationStagePlans.requestsDto";
import { EditSegmentsInputForm } from "../../containers/DealPage/components/Configuration/Segments/components/types";
import { EditVehiclesInputForm } from "../../containers/DealPage/components/Configuration/Transport/components/types";
import DeepPartial from "../../types/DeepPartial";
import { UpdateCargoRequestDto } from "../../api/apiServices/backOffice/controllers/dealCargo/dealCargo.requestsDto";
import { EditVehiclesRequestDto } from "../../api/apiServices/backOffice/controllers/dealVehicle/dealVehicle.requestsDto";
import TrackingSensorModel, { TrackingSensorDataConverter } from "../Tracking/TrackingSensorModel";
import { BitrixCompanyDataConverter, BitrixCompanyModel } from "../Dictionaries/BitrixCompanyModel";
import EntityModel from "../General/EntityModel";

export type DealLiteApplicationModel = Omit<DealLiteApplicationDto, "bitrixCompanyInfo"> & {
  bitrixCompanyInfo?: BitrixCompanyModel;
};

export type DealLiteModel = Omit<DealLiteDto, "creationDate" | "completionDate" | "closingDate" | "dealNotifications" | "application"> & {
  completionDate?: dayjs.Dayjs;
  closingDate?: dayjs.Dayjs;
  creationDate: dayjs.Dayjs;
  dealNotifications: Array<DealNotificationModel>;
  application: DealLiteApplicationModel;
};

export type OldDealSegmentModel = Omit<OldDealSegmentDto, "id"> & {
  id?: number;
};

export type DealSegmentModel = DealSegmentDto;

export type DealVehicleModel = Omit<DealVehicleDto, "flightDate" | "trackingObject" | "trackingCandidates" | "bitrixCompanyInfo"> & {
  flightDate?: dayjs.Dayjs;
  trackingObject?: TrackingSensorModel;
  trackingCandidates?: Array<TrackingSensorModel>;
  bitrixCompanyInfo?: BitrixCompanyModel;
};

export type DealTrackerModel = Omit<DealTrackerDto, "id" | "realLoadDate"> & {
  id?: number;
  realLoadDate: dayjs.Dayjs;
};

export type DealAccountServiceModel = Omit<DealAccountServiceDto, "id"> & {
  id?: number;
};

export type DealPaymentHistoryModel = Omit<DealPaymentHistoryDto, "changedAt" | "newPaymentDate"> & {
  changedAt: dayjs.Dayjs;
  newPaymentDate: dayjs.Dayjs;
};

export type DealAccountModel = Omit<
  DealAccountDto,
  | "id"
  | "serviceCosts"
  | "rateDate"
  | "creationDate"
  | "accountingDate"
  | "payments"
  | "documents"
  | "act"
  | "invoice"
  | "upd"
  | "issueDate"
  | "contractorAccountDate"
  | "cargoDeliveryDate"
  | "paymentDate"
  | "paymentDateHistory"
  | "contractor"
> & {
  id?: number;
  serviceCosts: Array<DealAccountServiceModel>;
  rateDate?: Date;
  creationDate: Date;
  accountingDate?: Date;
  payments: Array<AccountPaymentModel>;
  documents: Array<AttachDocumentFormModel>;
  act?: AccountClosingDocumentModel;
  invoice?: AccountClosingDocumentModel;
  upd?: AccountClosingDocumentModel;
  issueDate: Date;
  contractorAccountDate?: Date;
  cargoDeliveryDate?: Date;
  paymentDate?: Date;
  paymentDateHistory: Array<DealPaymentHistoryModel>;
  contractor: EntityModel & {
    name: string;
    contractorId: number;
    contractorContractId: number;
    bitrixCompanyInfo?: BitrixCompanyModel;
  };
};

export type DealAdditionalInfoModel = Omit<DealAdditionalInfoDto, "loadDate" | "arrivalDate" | "cargoUnloadingDate"> & {
  loadDate?: Date;
  arrivalDate?: Date;
  cargoUnloadingDate?: Date;
};

export type DealDocumentModel = Omit<DealDocumentDto, "id"> & {
  id?: number;
};

export type DealCargoPackingSubElementModel = DealCargoPackingSubElementDto;

export type DealCargoPackingElementModel = Omit<DealCargoPackingElementDto, "packingSubElements"> & {
  packingSubElements: Array<DealCargoPackingSubElementModel>;
};

export type DealCargoPackingListModel = Omit<DealCargoPackingListDto, "packingElements" | "shippingDate"> & {
  packingElements: Array<DealCargoPackingElementModel>;
  shippingDate: dayjs.Dayjs;
};
export type DealCargoOriginAssignmentModel = DealCargoOriginAssignmentDto;
export type DealCargoDestinationAssignmentModel = DealCargoDestinationAssignmentDto;

export type DealCargoModel = Omit<DealCargoDto, "packingLists" | "origins" | "destinations"> & {
  packingLists: Array<DealCargoPackingListModel>;
  origins: Array<DealCargoOriginAssignmentModel>;
  destinations: Array<DealCargoDestinationAssignmentModel>;
};

export type DealTransportationStageModel = Omit<DealTransportationStageDto, "plannedDate" | "actualDate"> & {
  plannedDate?: dayjs.Dayjs;
  actualDate?: dayjs.Dayjs;
};

export type DealTransportationStagePlanStageModel = Omit<
  DealTransportationStagePlanStageDto,
  "plannedDate" | "plannedDateHint" | "actualDate"
> & {
  plannedDate?: dayjs.Dayjs;
  plannedDateHint?: dayjs.Dayjs;
  actualDate?: dayjs.Dayjs;
};

export type DealTransportationStagePlanModel = Omit<DealTransportationStagePlanDto, "stages"> & {
  stages: Array<DealTransportationStagePlanStageModel>;
};

export type DealModel = Omit<
  DealDto,
  | "application"
  | "plannedCalculation"
  | "revenue"
  | "loadDate"
  | "arrivalDate"
  | "expense"
  | "creationDate"
  | "completionDate"
  | "cargoDeliveryDate"
  | "closingDate"
  | "documents"
  | "cargoUnloadingDate"
  | "trackers"
  | "overheadExpenses"
  | "dealNotifications"
  | "dealTransportationStages"
  | "dealTransportationStagePlans"
  | "cargo"
  | "segments"
  | "vehicles"
  | "trackingObjects"
> & {
  application?: ApplicationModel;
  specialClient?: BitrixCompanyModel;
  plannedCalculation: ApplicationCalculationModel;
  revenue: Array<DealAccountModel>;
  expense: Array<DealAccountModel>;
  cargo?: DealCargoModel;
  creationDate: Date;
  completionDate?: Date;
  cargoDeliveryDate?: Date;
  closingDate?: Date;
  documents: Array<DealDocumentModel>;
  trackers: Array<DealTrackerModel>;
  overheadExpenses: Array<OverheadExpenseModel>;
  dealNotifications: Array<DealNotificationModel>;
  dealTransportationStages: Array<DealTransportationStageModel>;
  dealTransportationStagePlans: Array<DealTransportationStagePlanModel>;
  segments: Array<DealSegmentModel>;
  vehicles: Array<DealVehicleModel>;
  trackingObjects: Array<TrackingSensorModel>;
} & DealAdditionalInfoModel;

export type DealAccountServiceModelUpdateType = Omit<DealAccountServiceDto, "id"> & {
  id?: number;
};

export type DealBasicInfoModel = DealBasicInfoDto;

export type DealAccountModelUpdateType = Omit<
  DealAccountDto,
  "id" | "serviceCosts" | "act" | "invoice" | "upd" | "companyId" | "paymentDateHistory" | "contractor" | "accountingDate"
> & {
  id?: number;
  serviceCosts: Array<DealAccountServiceModelUpdateType>;
  contractor: EntityModel & {
    name: string;
    contractorId: number;
    contractorContractId: number;
  };
};

export type DealTrackerUpdateType = Omit<DealTrackerDto, "id"> & {
  id?: number;
};

export type DealModelUpdateType = Omit<
  DealDto,
  | "application"
  | "plannedCalculation"
  | "revenue"
  | "expense"
  | "creationDate"
  | "documents"
  | "trackers"
  | "segments"
  | "overheadExpenses"
  | "dealNotifications"
  | "cargo"
  | "trackingObjects"
> & {
  revenue: Array<DealAccountModelUpdateType>;
  expense: Array<DealAccountModelUpdateType>;
  documents: Array<DealDocumentModel>;
  trackers: Array<DealTrackerUpdateType>;
};

type ShortDealModelToCalculateIndicators = {
  revenue: Array<{
    isMarkedForDeletion: boolean;
    currencyId: number;
    vat?: number | null;
    rate?: number;
    amountInBaseCurrency: number;
  }>;
  expense: Array<{
    isMarkedForDeletion: boolean;
    currencyId: number;
    vat?: number | null;
    rate?: number;
    amountInBaseCurrency: number;
  }>;
  overheadExpenses: Array<{ currencyId: number; amount: number }>;
  actualProfit: number;
};

export class DealDataConverter {
  // Model

  static convertAccountService(service: DealAccountServiceDto): DealAccountServiceModel {
    return {
      ...service,
    };
  }

  static convertAccountServices(services: Array<DealAccountServiceDto>): Array<DealAccountServiceModel> {
    return services.map((x) => this.convertAccountService(x));
  }

  static convertAccount({ account, host }: { account: DealAccountDto; host: "WEB" | "BITRIX" }): DealAccountModel {
    return {
      ...account,
      serviceCosts: this.convertAccountServices(account.serviceCosts ?? []),
      rateDate: account.rateDate ? dayjs(account.rateDate).toDate() : undefined,
      creationDate: new Date(account.creationDate),
      accountingDate: account.accountingDate ? new Date(account.accountingDate) : undefined,
      documents: AttachDocumentDataConverter.convertDocumentsToFormData(account.documents ?? [], host),
      payments: (account.payments ?? []).map((x) => ({ ...x, rateDate: x.rateDate ? new Date(x.rateDate) : undefined })),
      act: account.act ? AccountClosingDocumentDataConverter.convertClosingDocument(account.act) : undefined,
      invoice: account.invoice ? AccountClosingDocumentDataConverter.convertClosingDocument(account.invoice) : undefined,
      upd: account.upd ? AccountClosingDocumentDataConverter.convertClosingDocument(account.upd) : undefined,
      issueDate: new Date(account.issueDate),
      contractorAccountDate: account.contractorAccountDate ? new Date(account.contractorAccountDate) : undefined,
      cargoDeliveryDate: account.cargoDeliveryDate ? new Date(account.cargoDeliveryDate) : undefined,
      paymentDate: account.paymentDate ? new Date(account.paymentDate) : undefined,
      paymentDateHistory: (account.paymentDateHistory ?? []).map((x) => ({
        ...x,
        changedAt: dayjs(x.changedAt),
        newPaymentDate: dayjs(x.newPaymentDate),
      })),
      contractor: {
        ...account.contractor,
        bitrixCompanyInfo: account.contractor?.bitrixCompanyInfo
          ? BitrixCompanyDataConverter.convertContractor(account.contractor?.bitrixCompanyInfo)
          : undefined,
      },
    };
  }

  static convertAccounts({ accounts, host }: { accounts: Array<DealAccountDto>; host: "WEB" | "BITRIX" }): Array<DealAccountModel> {
    return accounts.map((x) => this.convertAccount({ account: x, host }));
  }

  static convertTracker(tracker: DealTrackerDto): DealTrackerModel {
    return { ...tracker, realLoadDate: dayjs(tracker.realLoadDate) };
  }

  static convertTrackers(trackers: Array<DealTrackerDto>): Array<DealTrackerModel> {
    return trackers.map((x) => this.convertTracker(x));
  }

  static convertTransportationStage(transportationStage: DealTransportationStageDto): DealTransportationStageModel {
    return {
      ...transportationStage,
      actualDate: transportationStage.actualDate ? dayjs(transportationStage.actualDate) : undefined,
      plannedDate: transportationStage.plannedDate ? dayjs(transportationStage.plannedDate) : undefined,
    };
  }

  static convertTransportationStages(transportationStages: Array<DealTransportationStageDto>): Array<DealTransportationStageModel> {
    return transportationStages.map((x) => this.convertTransportationStage(x));
  }

  static convertTransportationStagePlanStage(
    transportationStage: DealTransportationStagePlanStageDto,
  ): DealTransportationStagePlanStageModel {
    return {
      ...transportationStage,
      locationId: transportationStage.locationId ?? undefined,
      comment: transportationStage.comment ?? undefined,
      actualDate: transportationStage.actualDate ? dayjs(transportationStage.actualDate) : undefined,
      plannedDate: transportationStage.plannedDate ? dayjs(transportationStage.plannedDate) : undefined,
      plannedDateHint: transportationStage.plannedDateHint ? dayjs(transportationStage.plannedDateHint) : undefined,
    };
  }

  static convertTransportationStagePlans(plans: Array<DealTransportationStagePlanDto> = []): Array<DealTransportationStagePlanModel> {
    return plans.map((plan) => ({
      ...plan,
      dealTrackingSegmentId: plan.dealTrackingSegmentId ?? undefined,
      dealVehicleIds: plan.dealVehicleIds ?? [],
      stages: (plan.stages ?? []).map((stage) => this.convertTransportationStagePlanStage(stage)),
    }));
  }

  static convertVehicle(vehicle: DealVehicleDto): DealVehicleModel {
    return {
      ...vehicle,
      flightDate: vehicle.flightDate ? dayjs(vehicle.flightDate) : undefined,
      trackingObject: vehicle.trackingObject ? TrackingSensorDataConverter.convertTrackingSensor(vehicle.trackingObject) : undefined,
      trackingCandidates: vehicle.trackingCandidates
        ? TrackingSensorDataConverter.convertTrackingSensors(vehicle.trackingCandidates)
        : undefined,
      bitrixCompanyInfo: vehicle.bitrixCompanyInfo ? BitrixCompanyDataConverter.convertContractor(vehicle.bitrixCompanyInfo) : undefined,
    };
  }

  static convertVehicles(vehicles: Array<DealVehicleDto>): Array<DealVehicleModel> {
    return vehicles.map((x) => this.convertVehicle(x));
  }

  static convertPackingList(packingList: DealCargoPackingListDto): DealCargoPackingListModel {
    return {
      ...packingList,
      shippingDate: dayjs(packingList.shippingDate),
    };
  }

  static convertPackingLists(packingLists: Array<DealCargoPackingListDto>): Array<DealCargoPackingListModel> {
    return packingLists.map((x) => this.convertPackingList(x));
  }

  static convertCargo(cargo: DealCargoDto): DealCargoModel {
    return {
      ...cargo,
      packingLists: this.convertPackingLists(cargo.packingLists),
    };
  }

  // Для метода парсера (там не получится спарсить destinationMode, поэтому он передается отдельно из текущего состояния формы)
  static convertDeepPartialPackingListModelToEditForm(
    packingList: DeepPartial<DealCargoPackingListDto>,
    destinationMode?: DealCargoPackingDestinationMode,
  ): FormPackingList {
    return {
      id: packingList.id,
      number: packingList.number,
      shippingDate: packingList.shippingDate ? dayjs(packingList.shippingDate) : undefined,
      destinationMode,
      packingElements: packingList.packingElements?.map((element) => ({
        id: element.id,
        name: element.name,
        numberOfPlaces: element.numberOfPlaces,
        totalWeight: element.totalWeight,
        packingSubElements: element.packingSubElements?.map((subElement) => ({
          id: subElement.id,
          name: subElement.name,
          quantityInBox: subElement.quantityInBox,
        })),
      })),
    } as FormPackingList;
  }

  static convertCargoToEditForm(cargo: DealCargoModel): EditCargoInputForm {
    const cargoOrigin = cargo.origins.find((origin) => origin.level === "CARGO");
    const cargoDestination = cargo.destinations.find((destination) => destination.level === "CARGO");

    return {
      id: cargo.id,
      originMode: cargo.originMode,
      originLocation: cargoOrigin?.location,
      originAddress: cargoOrigin?.address,
      destinationMode: cargo.destinationMode,
      destinationLocation: cargoDestination?.location,
      destinationAddress: cargoDestination?.address,
      packingLists: cargo.packingLists.map((packingList) => {
        const packingOrigin = cargo.origins.find((origin) => origin.level === "PACKING" && origin.packingId === packingList.id);
        const packingDestination = cargo.destinations.find(
          (destination) => destination.level === "PACKING" && destination.packingId === packingList.id,
        );

        return {
          id: packingList.id,
          number: packingList.number,
          shippingDate: dayjs(packingList.shippingDate),
          destinationMode: packingList.destinationMode,
          originLocation: packingOrigin?.location,
          originAddress: packingOrigin?.address,
          destinationLocation: packingDestination?.location,
          destinationAddress: packingDestination?.address,
          packingElements: packingList.packingElements.map((element) => {
            const elementDestination = cargo.destinations.find(
              (destination) => destination.level === "ELEMENT" && destination.packingElementId === element.id,
            );

            return {
              id: element.id,
              name: element.name,
              numberOfPlaces: element.numberOfPlaces,
              totalWeight: element.totalWeight,
              destinationLocation: elementDestination?.location,
              destinationAddress: elementDestination?.address,
              packingSubElements: element.packingSubElements.map((subElement) => ({
                id: subElement.id,
                name: subElement.name,
                quantityInBox: subElement.quantityInBox,
              })),
            };
          }),
        };
      }),
    };
  }

  static convertSegmentsToEditForm(segments: Array<DealSegmentModel>): EditSegmentsInputForm {
    return {
      segments: segments.map((segment) => {
        if (segment.type === "BROADCAST") {
          return {
            id: segment.id,
            type: segment.type,
            carrierMode: segment.carrierMode ?? "PER_VEHICLE",
            originLocation: segment.origins[0],
          };
        }

        return {
          id: segment.id,
          type: segment.type,
          carrierMode: segment.carrierMode ?? "PER_VEHICLE",
          originLocations: segment.origins,
          destinationLocations: segment.destinations,
          waypoints: segment.waypoints ?? [],
          isFirstMile: segment.isFirstMile,
          isLastMile: segment.isLastMile,
          transportationTypeId: segment.transportationTypeId,
          borderCrossing: segment.borderCrossing,
        };
      }),
    };
  }

  static convertSegmentsForUpdate(form: EditSegmentsInputForm): SegmentsReplaceAllRequestDto {
    return form.segments.map((segment) => {
      if (segment.type === "BROADCAST") {
        return {
          id: segment.id,
          type: segment.type,
          carrierMode: segment.carrierMode,
          origins: [segment.originLocation.id],
          destinations: [],
          borderCrossingId: null,
        };
      }

      return {
        id: segment.id,
        type: segment.type,
        carrierMode: segment.carrierMode,
        origins: segment.originLocations.map((location) => location.id),
        destinations: segment.destinationLocations.map((location) => location.id),
        waypoints: segment.waypoints.map((location) => location.id),
        isFirstMile: segment.isFirstMile,
        isLastMile: segment.isLastMile,
        transportationTypeId: segment.transportationTypeId,
        borderCrossingId: segment.borderCrossing?.id ?? null,
      };
    });
  }

  static convertVehiclesToEditForm(vehicles: Array<DealVehicleModel>): EditVehiclesInputForm {
    return {
      vehicles: vehicles.map((vehicle) => ({
        id: vehicle.id,
        dealSegmentId: vehicle.dealSegmentId,
        vehicleTypeId: vehicle.vehicleTypeId,
        bitrixCompanyInfo: vehicle.bitrixCompanyInfo,
        autoNumber: vehicle.autoNumber,
        containerNumber: vehicle.containerNumber,
        railwayWaybillNumber: vehicle.railwayWaybillNumber,
        airWaybillNumber: vehicle.airWaybillNumber,
        flightNumber: vehicle.flightNumber,
        flightDate: vehicle.flightDate,
        imei: vehicle.imei,
        phoneNumber: vehicle.phoneNumber,
        externalSensorNumber: vehicle.externalSensorNumber,
        sensorOrdered: vehicle.sensorOrdered,
        sensorComment: vehicle.sensorComment,
        externalTrackingLink: vehicle.externalTrackingLink,
        packingElementIds: vehicle.packingElements.map((x) => x.id),
      })),
    };
  }

  static convertVehiclesFromFormForUpdate(form: EditVehiclesInputForm): Array<EditVehiclesRequestDto> {
    return form.vehicles.map((vehicle) => ({
      id: vehicle.id,
      dealSegmentId: vehicle.dealSegmentId,
      vehicleTypeId: vehicle.vehicleTypeId!,
      ...(vehicle.bitrixCompanyInfo?.id !== undefined ? { carrierId: vehicle.bitrixCompanyInfo.id } : {}),
      autoNumber: vehicle.autoNumber,
      containerNumber: vehicle.containerNumber,
      railwayWaybillNumber: vehicle.railwayWaybillNumber,
      airWaybillNumber: vehicle.airWaybillNumber,
      flightNumber: vehicle.flightNumber,
      flightDate: vehicle.flightDate ? vehicle.flightDate.format("YYYY-MM-DD") : undefined,
      imei: vehicle.imei,
      phoneNumber: vehicle.phoneNumber,
      externalSensorNumber: vehicle.externalSensorNumber,
      sensorOrdered: vehicle.sensorOrdered,
      sensorComment: vehicle.sensorComment,
      externalTrackingLink: vehicle.externalTrackingLink,
      packingElementIds: vehicle.packingElementIds,
    }));
  }

  static convertDeal({
    deal,
    transportCharacteristic,
    currency,
    host,
  }: {
    deal: DealDto;
    transportCharacteristic: TransportCharacteristicModel;
    currency?: CurrencyModel;
    host: "WEB" | "BITRIX";
  }): DealModel {
    return {
      ...deal,
      documents: deal.documents ?? [],
      overheadExpensesSumInBaseCurrency: deal.overheadExpensesSumInBaseCurrency ?? 0,
      segments: (deal.segments ?? []).map((segment) =>
        segment.type === "DELIVERY" ? { ...segment, waypoints: segment.waypoints ?? [] } : segment,
      ),
      revenue: this.convertAccounts({ accounts: deal.revenue ?? [], host }),
      expense: this.convertAccounts({ accounts: deal.expense ?? [], host }),
      application: deal.application
        ? ApplicationDataConverter.convertApplication(deal.application, transportCharacteristic, host, currency)
        : undefined,
      loadDate: deal.loadDate ? new Date(deal.loadDate) : undefined,
      arrivalDate: deal.arrivalDate ? new Date(deal.arrivalDate) : undefined,
      creationDate: new Date(deal.creationDate),
      completionDate: deal.completionDate ? new Date(deal.completionDate) : undefined,
      cargoDeliveryDate: deal.cargoDeliveryDate ? new Date(deal.cargoDeliveryDate) : undefined,
      closingDate: deal.closingDate ? new Date(deal.closingDate) : undefined,
      cargoUnloadingDate: deal.cargoUnloadingDate ? new Date(deal.cargoUnloadingDate) : undefined,
      plannedCalculation: ApplicationDataConverter.convertCalculation(deal.plannedCalculation),
      overheadExpenses: OverheadExpenseDataConverter.convertOverheadExpenses(deal.overheadExpenses ?? []),
      trackers: this.convertTrackers(deal.trackers ?? []),
      dealNotifications: DealNotificationDataConverter.convertDealNotifications(deal.dealNotifications ?? []),
      dealTransportationStages: this.convertTransportationStages(deal.dealTransportationStages ?? []),
      dealTransportationStagePlans: this.convertTransportationStagePlans(deal.dealTransportationStagePlans ?? []),
      cargo: deal.cargo ? this.convertCargo(deal.cargo) : undefined,
      vehicles: this.convertVehicles(deal.vehicles ?? []),
      trackingObjects: TrackingSensorDataConverter.convertTrackingSensors(deal.trackingObjects ?? []),
    };
  }

  static applyDealRoutes(deal: DealModel, routesDto: DealRoutesDto): DealModel {
    const plannedRoutesBySegmentId = new Map(routesDto.routes.plannedRoutes.map((route) => [route.segmentId, route.route]));
    const initialRoutesByVehicleId = new Map(routesDto.routes.initialRoutes.map((route) => [route.vehicleId, route.route]));
    const trackingObjects = this.convertSensorRoutesToTrackingObjects(routesDto);
    const trackingObjectsById = new Map(trackingObjects.map((trackingObject) => [trackingObject.id, trackingObject]));

    return {
      ...deal,
      segments: deal.segments.map((segment) => ({
        ...segment,
        plannedRoutePoints:
          segment.type === "DELIVERY" && segment.id !== undefined && plannedRoutesBySegmentId.has(segment.id)
            ? this.normalizePlannedRoutePoints(plannedRoutesBySegmentId.get(segment.id))
            : segment.type === "DELIVERY"
            ? segment.plannedRoutePoints
            : undefined,
      })),
      vehicles: deal.vehicles.map((vehicle) => {
        const trackingObject = vehicle.trackingObjectId ? trackingObjectsById.get(vehicle.trackingObjectId) : undefined;

        return {
          ...vehicle,
          trackingInitialRoutePoints:
            vehicle.id !== undefined && initialRoutesByVehicleId.has(vehicle.id)
              ? this.normalizeRoutePoints(initialRoutesByVehicleId.get(vehicle.id))
              : vehicle.trackingInitialRoutePoints,
          trackingObject: trackingObject ?? vehicle.trackingObject,
        };
      }),
      trackingObjects,
    };
  }

  static preserveDealRoutes(nextDeal: DealModel, previousDeal?: DealModel): DealModel {
    if (!previousDeal) {
      return nextDeal;
    }

    const previousSegmentsById = new Map(previousDeal.segments.map((segment) => [segment.id, segment]));
    const previousVehiclesById = new Map(previousDeal.vehicles.map((vehicle) => [vehicle.id, vehicle]));
    const previousTrackingObjects = previousDeal.trackingObjects ?? [];
    const previousTrackingObjectsById = new Map(previousTrackingObjects.map((sensor) => [sensor.id, sensor]));

    return {
      ...nextDeal,
      segments: nextDeal.segments.map((segment) => {
        const previousSegment = previousSegmentsById.get(segment.id);

        if (segment.type !== "DELIVERY") {
          return segment;
        }

        return {
          ...segment,
          plannedRoutePoints:
            segment.plannedRoutePoints ?? (previousSegment?.type === "DELIVERY" ? previousSegment.plannedRoutePoints : undefined),
        };
      }),
      vehicles: nextDeal.vehicles.map((vehicle) => {
        const previousVehicle = previousVehiclesById.get(vehicle.id);
        const sensorId = vehicle.trackingObject?.id ?? vehicle.trackingObjectId;
        const previousSensor = previousVehicle?.trackingObject;
        const previousTrackingObject = previousSensor?.id === sensorId ? previousSensor : undefined;

        return {
          ...vehicle,
          trackingInitialRoutePoints: vehicle.trackingInitialRoutePoints ?? previousVehicle?.trackingInitialRoutePoints,
          trackingObject: vehicle.trackingObject
            ? this.preserveTrackingSensorMileageHistory(vehicle.trackingObject, previousTrackingObject)
            : previousTrackingObject,
        };
      }),
      trackingObjects: nextDeal.trackingObjects.length
        ? nextDeal.trackingObjects.map((sensor) =>
            this.preserveTrackingSensorMileageHistory(sensor, previousTrackingObjectsById.get(sensor.id)),
          )
        : previousTrackingObjects,
    };
  }

  private static preserveTrackingSensorMileageHistory(
    sensor: TrackingSensorModel,
    previousSensor?: TrackingSensorModel,
  ): TrackingSensorModel {
    if (sensor.mileageHistory !== undefined || sensor.id !== previousSensor?.id) {
      return sensor;
    }

    return { ...sensor, mileageHistory: previousSensor.mileageHistory };
  }

  private static convertSensorRoutesToTrackingObjects(routesDto: DealRoutesDto): Array<TrackingSensorModel> {
    const sensors: Array<TrackingSensorDto> = routesDto.routes.sensorRoutes.map((sensorRoute) => ({
      id: sensorRoute.sensorId,
      adapterType: sensorRoute.adapterType,
      events: sensorRoute.events,
      lastSyncAt: sensorRoute.lastSyncAt,
      nextSyncAt: sensorRoute.nextSyncAt,
      metadata: sensorRoute.metadata ?? {},
      points: sensorRoute.route ?? [],
      paths: sensorRoute.paths ?? [],
      mileageHistory: sensorRoute.mileageHistory,
    }));

    return TrackingSensorDataConverter.convertTrackingSensors(sensors);
  }

  private static normalizePlannedRoutePoints(route?: DealPlannedRoutePointsDto): DealPlannedRoutePointsDto {
    return {
      firstMile: this.normalizeRoutePoints(route?.firstMile),
      main: this.normalizeRoutePoints(route?.main),
      lastMile: this.normalizeRoutePoints(route?.lastMile),
    };
  }

  private static normalizeRoutePoints(route: Array<DealPlannedRoutePointDto> = []): Array<DealPlannedRoutePointDto> {
    return route.map((point) => ({
      ...point,
      sorOrder: point.sorOrder ?? point.sortOrder,
    }));
  }

  static convertDeals({
    currencies,
    deals,
    host,
    transportCharacteristics,
  }: {
    deals: Array<DealDto>;
    transportCharacteristics: Array<TransportCharacteristicModel>;
    currencies: Array<CurrencyModel>;
    host: "WEB" | "BITRIX";
  }): Array<DealModel> {
    return deals.map((x) => {
      const transportCharacteristic = transportCharacteristics.find((t) => t.id === x.application?.filter.transportCharacteristicId)!;
      const currency = currencies.find((t) => t.id === x.application?.filter.currencyId)!;
      return this.convertDeal({ deal: x, transportCharacteristic, currency, host });
    });
  }

  // Update

  static convertAccountServiceForUpdate(serviceCost: DealAccountServiceModel): DealAccountServiceModelUpdateType {
    return {
      ...serviceCost,
    };
  }

  static convertAccountServicesForUpdate(serviceCosts: Array<DealAccountServiceModel>): Array<DealAccountServiceModelUpdateType> {
    return serviceCosts.map((x) => this.convertAccountServiceForUpdate(x));
  }

  static convertAccountForUpdate({
    rateDate,
    creationDate,
    accountingDate: _accountingDate,
    documents,
    payments,
    act: _act,
    invoice: _invoice,
    upd: _upd,
    issueDate,
    contractorAccountDate,
    cargoDeliveryDate,
    paymentDate,
    ...account
  }: DealAccountModel): DealAccountModelUpdateType {
    return {
      ...account,
      serviceCosts: this.convertAccountServicesForUpdate(account.serviceCosts),
      rateDate: rateDate ? dayjs(rateDate).format("YYYY-MM-DD") : null,
      creationDate: dayjs(creationDate).format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]"),
      documents: AttachDocumentDataConverter.convertDocumentsToUpdate(documents),
      payments: payments.map((x) => ({ ...x, rateDate: x.rateDate ? dayjs(x.rateDate).format("YYYY-MM-DD") : undefined })),
      issueDate: dayjs(issueDate).format("YYYY-MM-DD"),
      contractorAccountDate: contractorAccountDate ? dayjs(contractorAccountDate).format("YYYY-MM-DD") : contractorAccountDate,
      cargoDeliveryDate: cargoDeliveryDate ? dayjs(cargoDeliveryDate).format("YYYY-MM-DD") : cargoDeliveryDate,
      paymentDate: paymentDate ? dayjs(paymentDate).format("YYYY-MM-DD") : paymentDate,
    };
  }

  static convertAccountsForUpdate(expenses: Array<DealAccountModel>): Array<DealAccountModelUpdateType> {
    return expenses.filter((account) => account.kind !== "CASH_REIMBURSEMENT").map((x) => this.convertAccountForUpdate(x));
  }

  static convertTrackerForUpdate(tracker: DealTrackerModel): DealTrackerUpdateType {
    return { ...tracker, realLoadDate: tracker.realLoadDate.format("YYYY-MM-DD") };
  }

  static convertTrackersForUpdate(trackers: Array<DealTrackerModel>): Array<DealTrackerUpdateType> {
    return trackers.map((x) => this.convertTrackerForUpdate(x));
  }

  static convertTransportationStageForUpdate(transportationStage: DealTransportationStageModel): DealTransportationStageDto {
    return {
      ...transportationStage,
      actualDate: transportationStage.actualDate ? transportationStage.actualDate.format("YYYY-MM-DD") : undefined,
      plannedDate: transportationStage.plannedDate ? transportationStage.plannedDate.format("YYYY-MM-DD") : undefined,
    };
  }

  static convertTransportationStagesForUpdate(
    transportationStages: Array<DealTransportationStageModel>,
  ): Array<DealTransportationStageDto> {
    return transportationStages.map((x) => this.convertTransportationStageForUpdate(x));
  }

  static convertTransportationStagePlanStageForUpdate(
    stage: DealTransportationStagePlanStageModel,
  ): DealTransportationStagePlanStageUpdateRequestDto {
    return {
      id: stage.id,
      transportationStageId: stage.transportationStageId,
      stageOrder: stage.stageOrder,
      locationId: stage.locationId,
      locationRequired: stage.locationRequired,
      plannedDate: stage.plannedDate?.format("YYYY-MM-DD"),
      actualDate: stage.actualDate?.format("YYYY-MM-DD"),
      completed: stage.completed,
      required: stage.required,
      comment: stage.comment,
    };
  }

  static convertTransportationStagePlanForUpdate(plan: DealTransportationStagePlanModel): UpdateDealTransportationStagePlanRequestDto {
    return {
      planOrder: plan.planOrder,
      name: plan.name,
      dealVehicleIds: plan.dealVehicleIds,
      stages: plan.stages.map((stage) => this.convertTransportationStagePlanStageForUpdate(stage)),
    };
  }

  static convertTransportationStagePlanForCopy(
    plan: DealTransportationStagePlanModel,
    dealVehicleIds: Array<number>,
    copyPlannedDates: boolean,
    copyActualDates: boolean,
  ): CopyDealTransportationStagePlanRequestDto {
    return {
      dealTrackingSegmentId: plan.dealTrackingSegmentId,
      name: plan.name,
      dealVehicleIds: [...new Set(dealVehicleIds)],
      stages: plan.stages.map((stage) => ({
        transportationStageId: stage.transportationStageId,
        stageOrder: stage.stageOrder,
        locationId: stage.locationId,
        locationRequired: stage.locationRequired,
        required: stage.required,
        comment: stage.comment,
        plannedDate: copyPlannedDates ? stage.plannedDate?.format("YYYY-MM-DD") : undefined,
        plannedDateHint: copyPlannedDates && stage.plannedDate ? stage.plannedDateHint?.format("YYYY-MM-DD") : undefined,
        actualDate: copyActualDates ? stage.actualDate?.format("YYYY-MM-DD") : undefined,
      })),
    };
  }

  static convertCargoForUpdate(form: CreateCargoInputForm): CreateCargoRequestDto {
    const origins: UpdateCargoRequestDto["origins"] = [];
    if (form.originMode === "CARGO" && form.originLocation) {
      origins.push({
        level: "CARGO",
        locationId: form.originLocation.id,
        address: form.originAddress,
      });
    } else {
      form.packingLists.forEach((packingList, index) => {
        if (packingList.originLocation) {
          origins.push({
            level: "PACKING",
            locationId: packingList.originLocation.id,
            address: packingList.originAddress,
            packingIndex: index,
          });
        }
      });
    }

    const destinations: UpdateCargoRequestDto["destinations"] = [];

    if (form.destinationMode === "CARGO") {
      if (form.destinationLocation) {
        destinations.push({
          level: "CARGO",
          locationId: form.destinationLocation.id,
          address: form.destinationAddress,
        });
      }
    } else {
      form.packingLists.forEach((packingList, packingIndex) => {
        if (packingList.destinationMode === "ELEMENT") {
          packingList.packingElements.forEach((element, elementIndex) => {
            if (element.destinationLocation) {
              destinations.push({
                level: "ELEMENT",
                locationId: element.destinationLocation.id,
                address: element.destinationAddress,
                packingIndex,
                // productIndex: elementIndex,
                packingElementIndex: elementIndex,
              });
            }
          });
        } else {
          if (packingList.destinationLocation) {
            destinations.push({
              level: "PACKING",
              locationId: packingList.destinationLocation.id,
              address: packingList.destinationAddress,
              packingIndex,
            });
          }
        }
      });
    }

    return {
      originMode: form.originMode,
      origins,
      destinationMode: form.destinationMode,
      destinations,
      packingLists: form.packingLists.map((packingList) => ({
        id: packingList.id,
        destinationMode: packingList.destinationMode,
        shippingDate: packingList.shippingDate.format("YYYY-MM-DD"),
        number: packingList.number,
        packingElements: packingList.packingElements.map((element) => ({
          id: element.id,
          totalWeight: element.totalWeight,
          numberOfPlaces: element.numberOfPlaces,
          name: element.name,
          packingSubElements: element.packingSubElements.map((subElement) => ({
            id: subElement.id,
            name: subElement.name,
            quantityInBox: subElement.quantityInBox,
          })),
        })),
      })),
    };
  }

  static convertDealForUpdate({
    loadDate,
    arrivalDate,
    creationDate: _creationDate,
    completionDate,
    cargoDeliveryDate,
    closingDate,
    cargoUnloadingDate,
    trackingObjects: _trackingObjects,
    specialClient: _specialClient,
    segments: _segments,
    dealTransportationStagePlans: _dealTransportationStagePlans,
    ...deal
  }: DealModel): DealModelUpdateType {
    dayjs.extend(utc);
    return {
      ...deal,
      revenue: this.convertAccountsForUpdate(deal.revenue),
      expense: this.convertAccountsForUpdate(deal.expense),
      loadDate: loadDate ? dayjs(loadDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      arrivalDate: arrivalDate ? dayjs(arrivalDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      completionDate: completionDate ? dayjs(completionDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      cargoDeliveryDate: cargoDeliveryDate ? dayjs(cargoDeliveryDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      closingDate: closingDate ? dayjs(closingDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      cargoUnloadingDate: cargoUnloadingDate ? dayjs(cargoUnloadingDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSSSSSSSS[Z]") : undefined,
      vehicles: deal.vehicles.map(
        ({
          trackingCandidates: _trackingCandidates,
          trackingObject: _trackingObject,
          trackingInitialRoutePoints: _trackingInitialRoutePoints,
          trackingDataStatus: _trackingDataStatus,
          flightDate,
          ...vehicle
        }) => ({
          ...vehicle,
          flightDate: flightDate ? flightDate.format("YYYY-MM-DD") : undefined,
          bitrixCompanyInfo: undefined,
        }),
      ),
      trackers: this.convertTrackersForUpdate(deal.trackers),
      dealTransportationStages: this.convertTransportationStagesForUpdate(deal.dealTransportationStages),
    };
  }

  static convertDealLite(deal: DealLiteDto): DealLiteModel {
    return {
      ...deal,
      creationDate: dayjs(deal.creationDate),
      completionDate: deal.completionDate ? dayjs(deal.completionDate) : undefined,
      closingDate: deal.closingDate ? dayjs(deal.closingDate) : undefined,
      dealNotifications: DealNotificationDataConverter.convertDealNotifications(deal.dealNotifications || []),
      application: deal.application && {
        ...deal.application,
        bitrixCompanyInfo: deal.application.bitrixCompanyInfo
          ? BitrixCompanyDataConverter.convertContractor(deal.application.bitrixCompanyInfo)
          : undefined,
      },
    };
  }

  static convertDealsLite(deals: Array<DealLiteDto>): Array<DealLiteModel> {
    return deals.map((x) => this.convertDealLite(x));
  }
}

export class DealUtils {
  static isAccountHasClosingDocument(account: DealAccountModel) {
    return Boolean(account.act || account.invoice || account.upd || account.closingDocumentTypes.length > 0);
  }

  static isAccountDisabled(account: DealAccountModel) {
    if (account.kind === "CASH_REIMBURSEMENT") return true;
    const hasClosingDocuments = this.isAccountHasClosingDocument(account);
    return account.payments.length > 0 || hasClosingDocuments;
  }

  static isDealHasDisabledAccount(deal: DealModel) {
    const disabledAccounts = deal.revenue.concat(deal.expense).filter((x) => this.isAccountDisabled(x));
    return disabledAccounts.length > 0;
  }

  static isDealHasDisabledRevenueAccount(deal: DealModel) {
    const disabledAccounts = deal.revenue.filter((x) => this.isAccountDisabled(x));
    return disabledAccounts.length > 0;
  }

  static getDealStatusLabelByStatus(status: DealStatus, lng: SupportedLanguages): string {
    switch (status) {
      case "BOOKING":
        return i18n.t("deal_created", { lng, ns: "translations" });
      case "IN_PROGRESS":
        return i18n.t("deal_in_progress", { lng, ns: "translations" });
      case "CARGO_DELIVERED":
        return i18n.t("deal_cargo_delivered", { lng, ns: "translations" });
      case "COMPLETED":
        return i18n.t("deal_completed", { lng, ns: "translations" });
      case "CLOSED":
        return i18n.t("deal_closed", { lng, ns: "translations" });
      case "CANCELLED":
        return i18n.t("deal_cancelled", { lng, ns: "translations" });
      default:
        return exhaustiveCheck(status);
    }
  }

  static getDealStatusLabelByStatusToExcel(
    status: DealStatus,
    lng: SupportedLanguages,
  ): { label: string; cellColor: string; labelColor: string } {
    switch (status) {
      case "BOOKING":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: gray[9].replaceAll("#", ""),
          cellColor: "fafafa",
        };
      case "IN_PROGRESS":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: blue[6].replaceAll("#", ""),
          cellColor: blue[3].replaceAll("#", ""),
        };
      case "CARGO_DELIVERED":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: gold[6].replaceAll("#", ""),
          cellColor: gold[1].replaceAll("#", ""),
        };
      case "COMPLETED":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: purple[7].replaceAll("#", ""),
          cellColor: purple[1].replaceAll("#", ""),
        };
      case "CLOSED":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: green[6].replaceAll("#", ""),
          cellColor: green[1].replaceAll("#", ""),
        };
      case "CANCELLED":
        return {
          label: this.getDealStatusLabelByStatus(status, lng),
          labelColor: red[5].replaceAll("#", ""),
          cellColor: red[1].replaceAll("#", ""),
        };
      default:
        return exhaustiveCheck(status);
    }
  }

  static getDealAmonalyLabelByAnomaly(anomaly: DealAnomalyType): string {
    switch (anomaly) {
      case "DELAY_IN_RECEIPT_OF_FUNDS":
        return i18n.t("delay_in_reciept_fund");
      case "RECEIPT_DATES_CHANGED_MORE_THAN_TWICE":
        return i18n.t("reciept_dates_changed_more_than_twice");
      case "PAYMENT_DELAYED":
        return i18n.t("payment_delayes");
      case "NEGATIVE_PROFIT":
        return i18n.t("negative_profit");
      case "SIGNIFICANT_PROFIT_VARIANCE":
        return i18n.t("signeficant_profit_variance");
      case "DEAL_NOT_CLOSED_OVER_3_MONTHS":
        return i18n.t("deal_not_closed_over_three_month");
      default:
        return exhaustiveCheck(anomaly);
    }
  }

  static getAccountTypeLabelByType(type: DealAccountType): string {
    switch (type) {
      case "REVENUE":
        return i18n.t("revenue");
      case "EXPENSE":
        return i18n.t("expense");
      default:
        return exhaustiveCheck(type);
    }
  }

  static getAccountStatusLabelByStatus(status: DealAccountStatus, lng: SupportedLanguages): string {
    switch (status) {
      case "PAID":
        return i18n.t("paid", { lng });
      case "NOT_PAID":
        return i18n.t("not_paid", { lng });
      case "PARTIALLY_PAID":
        return i18n.t("partially_pain", { lng });
      default:
        return exhaustiveCheck(status);
    }
  }

  static calculateDealActualRevenue(deal: DealModel): number {
    return deal.revenue
      .filter((x) => !x.isMarkedForDeletion)
      .reduce<number>((acc, x) => {
        if (x.amountInBaseCurrency) {
          return acc + x.amountInBaseCurrency;
        }
        return acc;
      }, 0);
  }

  static calculateDealActualExpense(deal: ShortDealModelToCalculateIndicators): number {
    return deal.expense
      .filter((x) => !x.isMarkedForDeletion)
      .reduce<number>((acc, x) => {
        if (x.amountInBaseCurrency) {
          return acc + x.amountInBaseCurrency;
        }
        return acc;
      }, 0);
  }

  static calculateDealOverheadExpense(deal: ShortDealModelToCalculateIndicators, currencies: Array<CurrencyModel>): number {
    return deal.overheadExpenses.reduce<number>((acc, x) => {
      const currency = currencies.find((c) => c.id === x.currencyId)!;
      return acc + (x.amount * currency.rate) / currency.nominal;
    }, 0);
  }

  static calculateDealMarginality(deal: ShortDealModelToCalculateIndicators, currencies: Array<CurrencyModel>): number {
    const actualProfit = deal.actualProfit;
    const expense = this.calculateDealActualExpense(deal);
    const overheadExpense = this.calculateDealOverheadExpense(deal, currencies);
    return actualProfit / (expense + overheadExpense);
  }

  static getDealBreadcrumbTitle(deal: DealModel, lng: SupportedLanguages) {
    return `${i18n.t("deal", { lng })}: ${deal.number}`;
  }

  static calculateAccountSummaryValues(account: DealAccountModel) {
    if (account.kind === "CASH_REIMBURSEMENT") {
      const amount = account.isMarkedForDeletion ? 0 : account.amountInBaseCurrency;
      return { amount, vatAmount: 0, withoutVatAmount: amount };
    }
    return account.serviceCosts.reduce<{
      amount: number;
      vatAmount: number;
      withoutVatAmount: number;
    }>(
      (acc, x) => {
        if (x.costInBaseCurrency) {
          acc.amount += x.costInBaseCurrency;
          const { vat: vatAmount, withoutVat } = TableUtils.calculateVats({
            cost: x.costInBaseCurrency,
            vat: VatUtils.getVatValue(account.vat),
          });
          acc.vatAmount += vatAmount;
          acc.withoutVatAmount += withoutVat;
        }

        return acc;
      },
      {
        amount: 0,
        vatAmount: 0,
        withoutVatAmount: 0,
      },
    );
  }

  static calculateAccountsSummaryValues(accounts: Array<DealAccountModel>) {
    return accounts.reduce<{
      amount: number;
      vatAmount: number;
      withoutVatAmount: number;
    }>(
      (acc, x) => {
        const { amount, vatAmount, withoutVatAmount } = this.calculateAccountSummaryValues(x);
        return {
          amount: acc.amount + amount,
          vatAmount: acc.vatAmount + vatAmount,
          withoutVatAmount: acc.withoutVatAmount + withoutVatAmount,
        };
      },
      { amount: 0, vatAmount: 0, withoutVatAmount: 0 },
    );
  }

  static accountHasVat(account: DealAccountModel) {
    return VatUtils.hasVat(account.vat);
  }

  static accountsHasVat(accounts: Array<DealAccountModel>) {
    return !!accounts.find((x) => this.accountHasVat(x));
  }
}

export default DealModel;
